import { MarkdownPlugin } from '@platejs/markdown'
import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { Plate, PlateContainer, PlateContent, usePlateEditor } from 'platejs/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AutoformatKit } from '~/features/editor/plugins/autoformat-kit'
import { BasicMarksKit } from '~/features/editor/plugins/basic-marks-kit'
import { FloatingToolbarKit } from '~/features/editor/plugins/floating-toolbar-kit'
import { ListKit } from '~/features/editor/plugins/list-kit'
import { MarkdownKit } from '~/features/editor/plugins/markdown-kit'
import { relativeDate } from '~/helpers/dateHelper'
import { useCurrentUser } from '~/hooks'
import { EvaluationCriteriaDisputeMutators } from '~/mutators/evaluation-criteria-disputes'
import { RouterOutput, useTRPC } from '~/services/trpc'
import { useReturnTo } from '~/templates/settings/hooks'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AlertDialog,
  AlertDialogTrigger,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading3,
  Icon,
  Multiline,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'
import { versionedScaleOptions } from '@hap/constants'
import { toPercent } from '@hap/utils'

import { usePermission } from '../access-control'
import { LinkButton } from '../navigation'
import { ScorecardCriteriaItem } from './scorecard-criteria-item'

export function Evaluations(props: { contactLogId: ID; userId: Nullable<ID> }) {
  const trpc = useTRPC()
  const { data: evaluations } = useQuery(
    trpc.evaluations.getByContactLogId.queryOptions(props.contactLogId),
  )

  return evaluations?.length ? (
    <div>
      {evaluations?.map((evaluation) => (
        <Evaluation
          key={evaluation.id}
          evaluation={evaluation}
          contactLogId={props.contactLogId}
          userId={props.userId}
        />
      ))}
    </div>
  ) : null
}

function Evaluation({
  evaluation,
  contactLogId,
  userId,
}: {
  evaluation: RouterOutput['evaluations']['getByContactLogId'][0]
  contactLogId: ID
  userId: Nullable<ID>
}) {
  const trpc = useTRPC()
  const hasEditPermission = usePermission({ resource: 'scorecards', actions: ['manage'] })
  const editorRef = useRef<HTMLElement>(null)
  const [editing, setEditing] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const returnTo = useReturnTo()
  const editor = usePlateEditor({
    plugins: [
      ...BasicMarksKit,
      ...MarkdownKit,
      ...ListKit,
      ...AutoformatKit,
      ...FloatingToolbarKit,
    ],
    value: (editor) =>
      editor.getApi(MarkdownPlugin).markdown.deserialize(evaluation.notes ?? ''),
  })
  const isPublished = !!evaluation.publishedAt
  const canManageEvaluation = usePermission({ resource: 'evaluate', actions: ['manage'] })
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [resolveNote, setResolveNote] = useState('')
  const [resolvingDisputeId, setResolvingDisputeId] = useState<null | string>(null)
  const [finalAwardedPoints, setFinalAwardedPoints] = useState<null | number>(null)
  const currentUser = useCurrentUser()
  const isAssignedAgent = currentUser?.id === userId
  const canDispute = isPublished && (isAssignedAgent || canManageEvaluation)

  const { data: disputes = [] } = useQuery(
    trpc.evaluationCriteriaDisputes.listByEvaluationId.queryOptions(evaluation.id),
  )

  const { resolve, isPending: isPendingResolve } =
    EvaluationCriteriaDisputeMutators.useResolve({
      contactLogId,
      evaluationId: evaluation.id,
    })

  const activeDisputes = disputes.filter((dispute) => !dispute.resolvedAt)
  const resolvedDisputes = disputes
    .filter((dispute) => !!dispute.resolvedAt)
    .sort((a, b) => {
      if (!a.resolvedAt || !b.resolvedAt) return 0
      return new Date(b.resolvedAt).getTime() - new Date(a.resolvedAt).getTime()
    })
  const criteriaByAssignmentId = evaluation.scorecard.sections
    .flatMap((section) => section.criteria)
    .reduce<
      Record<string, (typeof evaluation.scorecard.sections)[number]['criteria'][number]>
    >((acc, criterion) => ({ ...acc, [criterion.id]: criterion }), {})

  const openingDispute = activeDisputes.find((d) => d.id === resolvingDisputeId) || null
  const openingCriterion = openingDispute
    ? criteriaByAssignmentId[openingDispute.scorecardCriteriaAssignmentId]
    : null
  const resolveScaleOptions = openingCriterion
    ? versionedScaleOptions[20250231][openingCriterion.scale]
    : []

  const openResolveDialog = (
    dispute: RouterOutput['evaluationCriteriaDisputes']['listByEvaluationId'][number],
  ) => {
    setResolvingDisputeId(dispute.id)
    setFinalAwardedPoints(dispute.proposedAwardedPoints)
    setResolveNote('')
    setResolveDialogOpen(true)
  }

  const submitResolve = async () => {
    if (!resolvingDisputeId) return
    await resolve({
      disputeId: resolvingDisputeId,
      finalAwardedPoints,
      resolutionNote: resolveNote,
    })
    setResolveDialogOpen(false)
    setResolvingDisputeId(null)
    setResolveNote('')
  }

  const formatAwardedPointsLabel = useCallback(
    (criteriaAssignmentId: string, awardedPoints: null | number) => {
      if (awardedPoints === null || awardedPoints === undefined) return 'N/A'
      const criterion = criteriaByAssignmentId[criteriaAssignmentId]
      if (!criterion) return `${awardedPoints}`
      const option = versionedScaleOptions[20250231][criterion.scale].find(
        (scaleOption) => scaleOption.value === awardedPoints,
      )
      return option?.label || `${awardedPoints}`
    },
    [criteriaByAssignmentId],
  )

  useEffect(() => {
    if (evaluation.notes) {
      editor.tf.setValue(
        editor.getApi(MarkdownPlugin).markdown.deserialize(evaluation.notes),
      )
    }
  }, [evaluation.notes])

  const { mutate: submitEvaluation, isPending } = useMutation(
    trpc.evaluations.submit.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(contactLogId),
        )
      },
    }),
  )

  const { mutateAsync: archiveEvaluation } = useMutation(
    trpc.evaluations.archive.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(contactLogId),
        )
      },
    }),
  )

  const { mutate: requeueEvaluation, isPending: isPendingRequeue } = useMutation(
    trpc.evaluations.requeue.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(contactLogId),
        )
      },
    }),
  )

  const { mutate: toggleReview, isPending: isSetting } = useMutation(
    trpc.evaluations.toggleReview.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(contactLogId),
        )
      },
    }),
  )

  const { mutate: updateEvaluation } = useMutation(
    trpc.evaluations.update.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(contactLogId),
        )
      },
    }),
  )

  const renderPassFailLabel = useCallback(
    (evaluation: RouterOutput['evaluations']['getByContactLogId'][0]) => {
      if (!evaluation.publishedAt) return <Badge variant="warn">Draft</Badge>
      if (evaluation.autoFail) return 'Auto Fail'
      return evaluation.metGoal ? 'Passed' : 'Failed'
    },
    [],
  )

  return (
    <div className="relative mb-6">
      <div className="mb-2 text-lg font-medium">
        {hasEditPermission ? (
          <Tooltip>
            <TooltipTrigger>
              <Link
                from="/$alias/contact-logs/$contactLogId"
                to="/$alias/settings/scorecards/$scorecardId/criteria"
                params={() => ({
                  scorecardId: evaluation.scorecard.id,
                })}
                search={{ returnTo }}
                className="hover:bg-accent inline-flex items-center gap-2 rounded-md px-2 py-1"
              >
                <Icon name="medal" /> {evaluation.scorecard.name}
              </Link>
            </TooltipTrigger>
            <TooltipContent>Edit Scorecard</TooltipContent>
          </Tooltip>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-md px-2 py-1">
            <Icon name="medal" /> {evaluation.scorecard.name}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <div className="bg-accent grid grid-cols-[auto_auto_1fr] items-center rounded-t-md border-b text-center">
          <div className="border-border border-r p-4 text-left">
            <div className="text-xl font-bold">{renderPassFailLabel(evaluation)}</div>
            <div>Goal ({toPercent(evaluation.goal)})</div>
          </div>
          <div className="border-border border-r px-8 py-4">
            <div className="text-xl font-bold">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="shrink-0 text-right text-xl font-bold">
                    {toPercent(evaluation.score)}
                  </div>
                </TooltipTrigger>
                {evaluation.autoFail && (
                  <TooltipContent>
                    Unadjusted Score: {toPercent(evaluation.realScore)}
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
            <div>Score</div>
          </div>

          <div className="flex items-center justify-end gap-1 p-4 pr-0">
            {evaluation.auto && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn({
                      'group relative': true,
                      'bg-warn/10 hover:bg-warn': evaluation.requeuedAt,
                    })}
                    disabled={isPendingRequeue}
                    onClick={() => {
                      requeueEvaluation({ evaluationId: evaluation.id })
                    }}
                  >
                    <Icon
                      name="arrows-clockwise"
                      className={cn({
                        'text-warn animate-spin duration-2000 group-hover:opacity-0':
                          evaluation.requeuedAt,
                      })}
                    />
                    {evaluation.requeuedAt && (
                      <Icon
                        name="x"
                        className="bg-warn text-warn-foreground absolute top-1/2 left-1/2 -translate-1/2 scale-0 opacity-0 transition-transform group-hover:scale-100 group-hover:opacity-100"
                      />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {evaluation.requeuedAt ? 'Cancel job' : 'Re-evaluate'}
                </TooltipContent>
              </Tooltip>
            )}
            {isPublished && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={evaluation.reviewedBy ? 'ghost-active' : 'ghost'}
                    size="icon"
                    icon="user-circle-check"
                    iconWeight={evaluation.reviewedBy ? 'fill' : undefined}
                    disabled={isSetting}
                    onClick={() => toggleReview(evaluation.id)}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {evaluation.reviewedBy ? (
                    <div className="grow">
                      Reviewed by {evaluation.reviewedBy.firstName}{' '}
                      {evaluation.reviewedBy.lastName}{' '}
                      {relativeDate(evaluation.reviewedAt)}
                    </div>
                  ) : (
                    'Mark as reviewed'
                  )}
                </TooltipContent>
              </Tooltip>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" icon="dots-three-outline" iconWeight="fill" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!!evaluation.publishedAt && (
                  <DropdownMenuItem
                    icon="printer"
                    onSelect={() => {
                      navigate({
                        to: '/$alias/print/evaluations/$evaluationId',
                        from: '/$alias',
                        params: { evaluationId: evaluation.id },
                      })
                    }}
                  >
                    Print evaluation
                  </DropdownMenuItem>
                )}
                <AlertDialog
                  title="Archive Evaluation"
                  description="Archiving this evaluation will cause it to be removed from reporting."
                  action={{
                    value: 'Archive',
                    variant: 'destructive',
                    async onClick() {
                      await archiveEvaluation({ evaluationId: evaluation.id })
                    },
                  }}
                >
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem
                      icon="trash"
                      variant="destructive"
                      onSelect={(e) => {
                        e.preventDefault()
                      }}
                    >
                      Archive evaluation
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                </AlertDialog>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {evaluation.requeuedAt && (
          <div className="bg-warn/10 text-warn p-4 text-center">
            Queued to be re-evaluated{' '}
            {formatDistanceToNow(new Date(evaluation.requeuedAt), {
              includeSeconds: true,
            })}{' '}
            ago.
          </div>
        )}

        {isPublished && (
          <div className="grid grid-cols-[auto_1fr] items-center border-b">
            <div className="text-foreground/50 border-border p-4">Evaluated on</div>
            <div className="p-4 font-medium">{relativeDate(evaluation.publishedAt)}</div>
          </div>
        )}

        {!!activeDisputes.length && (
          <div className="space-y-2 border-b p-4">
            <div className="flex items-center gap-2 font-medium">
              <Icon name="flag" className="text-warn" />
              {activeDisputes.length} open dispute{activeDisputes.length === 1 ? '' : 's'}
            </div>
            <div className="space-y-2">
              {activeDisputes.map((dispute) => {
                const criterion =
                  criteriaByAssignmentId[dispute.scorecardCriteriaAssignmentId]
                return (
                  <div
                    key={dispute.id}
                    className="bg-accent/40 flex items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="text-sm">
                      <div className="font-medium">
                        {criterion?.name || 'Criteria item'}
                      </div>
                      <div className="text-foreground/70">
                        Proposed: {dispute.proposedAwardedPoints ?? 'N/A'}
                        {dispute.note ? ` - ${dispute.note}` : ''}
                      </div>
                    </div>
                    {canManageEvaluation && (
                      <Button size="sm" onClick={() => openResolveDialog(dispute)}>
                        Resolve
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {!!resolvedDisputes.length && (
          <Accordion type="single" collapsible className="border-b">
            <AccordionItem value="resolved-disputes" className="border-b-0">
              <AccordionTrigger className="px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="check" className="text-success" />
                  {resolvedDisputes.length} resolved dispute
                  {resolvedDisputes.length === 1 ? '' : 's'}
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-2 px-4 pb-4">
                {resolvedDisputes.map((dispute) => {
                  const criterion =
                    criteriaByAssignmentId[dispute.scorecardCriteriaAssignmentId]
                  return (
                    <div key={dispute.id} className="rounded-md border p-3 text-sm">
                      <div className="font-medium">
                        {criterion?.name || 'Criteria item'}
                      </div>
                      <div className="text-foreground/70">
                        Current:{' '}
                        {formatAwardedPointsLabel(
                          dispute.scorecardCriteriaAssignmentId,
                          dispute.currentAwardedPoints,
                        )}
                        {' -> '}Proposed:{' '}
                        {formatAwardedPointsLabel(
                          dispute.scorecardCriteriaAssignmentId,
                          dispute.proposedAwardedPoints,
                        )}
                        {' -> '}Final:{' '}
                        {formatAwardedPointsLabel(
                          dispute.scorecardCriteriaAssignmentId,
                          dispute.resolvedAwardedPoints,
                        )}
                      </div>
                      {dispute.note && (
                        <div className="text-foreground/70 mt-1">
                          Agent note: {dispute.note}
                        </div>
                      )}
                      {dispute.resolutionNote && (
                        <div className="text-foreground/70 mt-1">
                          Resolution note: {dispute.resolutionNote}
                        </div>
                      )}
                      {dispute.resolvedAt && (
                        <div className="text-foreground/60 mt-1 text-xs">
                          Resolved by{' '}
                          {dispute.resolvedByUser
                            ? `${dispute.resolvedByUser.firstName} ${dispute.resolvedByUser.lastName}`
                            : 'Unknown user'}{' '}
                          {relativeDate(dispute.resolvedAt)}
                        </div>
                      )}
                    </div>
                  )
                })}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {evaluation.notes && (
          <div className="group p-4 pb-0">
            <header className="flex items-center">
              <Heading3 className="grow">Summary</Heading3>
              {hasEditPermission &&
                (editing ? (
                  <div className="space-x-2">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEditing(false)
                        editor.tf.setValue(
                          editor
                            .getApi(MarkdownPlugin)
                            .markdown.deserialize(evaluation.notes ?? ''),
                        )
                      }}
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setEditing(false)
                        updateEvaluation({
                          evaluationId: evaluation.id,
                          values: {
                            notes: editor.api.markdown.serialize(),
                          },
                        })
                      }}
                      size="sm"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button
                    icon="pencil-simple"
                    size="sm"
                    variant="secondary"
                    className="shrink-0 opacity-0 group-hover:opacity-100"
                    onClick={() => {
                      setEditing(true)
                      editorRef.current?.focus()
                    }}
                  >
                    Edit summary
                  </Button>
                ))}
            </header>
            {/* <Markdown
              components={{
                p: ({ children }) => <p className="mb-4">{children}</p>,
                h3: ({ children }) => (
                  <h3 className="mb-4 text-xl font-semibold">{children}</h3>
                ),
                li: ({ children }) => <li className="mb-1">{children}</li>,
                ul: ({ children }) => <ul className="mb-4 ml-5 list-disc">{children}</ul>,
              }}
            >
              {evaluation.notes}
            </Markdown> */}
            <div className="relative -mx-4">
              <Plate editor={editor}>
                <PlateContainer className="caret-primary selection:bg-brand/25 [&_.slate-selection-area]:border-brand/25 [&_.slate-selection-area]:bg-brand/15', relative w-full cursor-text overflow-y-auto select-text focus-visible:outline-none [&_.slate-selection-area]:z-50 [&_.slate-selection-area]:border">
                  <PlateContent
                    ref={editorRef}
                    style={{ minHeight: '100px', padding: '1em' }}
                    className="group/editor ring-offset-background placeholder:text-muted-foreground/80 **:data-slate-placeholder:text-muted-foreground/80 relative w-full cursor-text overflow-x-hidden rounded-md break-words whitespace-pre-wrap select-text focus-visible:outline-none **:data-slate-placeholder:!top-1/2 **:data-slate-placeholder:-translate-y-1/2 **:data-slate-placeholder:opacity-100! [&_strong]:font-bold"
                    placeholder="Type your summary here..."
                    onFocus={() => setEditing(true)}
                    readOnly={!hasEditPermission}
                  />
                </PlateContainer>
              </Plate>
            </div>

            {evaluation.outcomes && (
              <Accordion type="multiple">
                <AccordionItem value="strengths">
                  <AccordionTrigger>⭐️ Strengths</AccordionTrigger>
                  <AccordionContent>
                    <ul className="mb-4 ml-8 list-disc space-y-4">
                      {evaluation.outcomes?.strengths.map((strength, key) => (
                        <li key={key}>
                          <strong>{strength.title}: </strong>
                          {strength.evidence}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="weakness">
                  <AccordionTrigger>🌱 Areas of improvement</AccordionTrigger>
                  <AccordionContent>
                    <ul className="mb-4 ml-8 list-disc space-y-4">
                      {evaluation.outcomes?.weaknesses.map((weakness, key) => (
                        <li key={key}>
                          <strong>{weakness.title}: </strong>
                          {weakness.recommendation}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="next-steps" className="border-b-0">
                  <AccordionTrigger>🏃 Next steps</AccordionTrigger>
                  <AccordionContent>
                    <ul className="mb-4 ml-8 list-disc space-y-4">
                      {evaluation.outcomes?.actionItems.map((action, key) => (
                        <li key={key}>{action}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        )}

        <div className="relative">
          {!isPublished && evaluation.auto && (
            <div className="bg-background/80 absolute inset-0 z-20 flex items-center justify-center text-center">
              <div>
                <Spinner size={40} />
                <div className="text-accent-foreground mt-4 font-semibold">Grading</div>
              </div>
            </div>
          )}

          <Accordion
            type="multiple"
            className="border-t"
            defaultValue={evaluation.scorecard.sections
              .filter((s) => {
                if (!evaluation.publishedAt) return true
                return s.criteria.some((c) => {
                  return evaluation.scores.find(
                    (s) => s.scorecardCriteriaAssignmentId === c.id && s.score !== null,
                  )
                })
              })
              .map((s) => s.id)}
          >
            {evaluation.scorecard.sections.map((section) => (
              <AccordionItem
                value={section.id}
                key={section.id}
                className="border-b last:border-b-0"
              >
                <AccordionTrigger className="text-foreground/70 bg-accent w-full p-2 px-4 text-center font-normal">
                  {section.name}
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  {section.criteria.length ? (
                    <ul className="group/section" data-auto-fail={evaluation.autoFail}>
                      {section.criteria.map((criteria) => {
                        const score = evaluation.scores.find(
                          (s) => s.scorecardCriteriaAssignmentId === criteria.id,
                        )
                        return (
                          <ScorecardCriteriaItem
                            key={criteria.id}
                            criteria={criteria}
                            score={score}
                            evaluationId={evaluation.id}
                            contactLogId={contactLogId}
                            published={isPublished}
                            workspaceId={evaluation.scorecard.workspaceId}
                            userId={userId}
                            autoEvaluation={evaluation.auto}
                            activeDispute={activeDisputes.find(
                              (dispute) =>
                                dispute.scorecardCriteriaAssignmentId === criteria.id,
                            )}
                          />
                        )
                      })}
                    </ul>
                  ) : (
                    <h4 className="text-foreground/80 py-4 text-center">
                      No criteria items
                    </h4>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
            {!isPublished && !evaluation.auto && (
              <>
                <ul>
                  <li>Awarded Points: {evaluation.awardedPoints}</li>
                  <li>Total Points: {evaluation.totalPoints}</li>
                </ul>
                <Button
                  onClick={() => submitEvaluation({ evaluationId: evaluation.id })}
                  disabled={isPending}
                  className="sticky right-0 bottom-0 left-0 z-20 w-full"
                >
                  Finalize Scorecard
                </Button>
              </>
            )}
            {canDispute && (
              <div className="grid p-2">
                <LinkButton
                  from="/$alias/interaction/$contactLogId/"
                  to="./dispute/$evaluationId"
                  variant="secondary"
                  params={{ evaluationId: evaluation.id }}
                >
                  Dispute Evaluation
                </LinkButton>
              </div>
            )}
          </Accordion>
        </div>

        <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolve dispute</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {openingCriterion && (
                <div>
                  <div className="mb-2 text-sm font-medium">{openingCriterion.name}</div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={finalAwardedPoints === null ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFinalAwardedPoints(null)}
                    >
                      N/A
                    </Button>
                    {resolveScaleOptions.map((option) => (
                      <Button
                        key={option.value}
                        variant={
                          finalAwardedPoints === option.value ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => setFinalAwardedPoints(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-medium">Resolution note (optional)</p>
                <Multiline
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  minRows={3}
                  placeholder="Add context for this resolution"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
                Cancel
              </Button>
              <Button loading={isPendingResolve} onClick={submitResolve}>
                Resolve and apply score
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
